import * as React from 'react';
import { IComparisonResult } from '../../../data/providers/reports/comparisonReportProvider';
import { Entity } from '../../../data/types';
import { roundValue } from '../../../utils/math';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardHeader } from '../../shared/card/CardHeader';
import { Metric } from '../../shared/Metric';
import { Notable } from '../../shared/pdf/Notable';
import { EPdfMode, isPdf, shouldInvert } from '../../shared/pdf/Pdf';
import { DataCell, DataColumn, LabelCell } from '../utils/Table';

interface IProps {
  result: IComparisonResult;
  colors: string[];
  pdfMode: EPdfMode;
  entity: Entity;
  comparisonEntityName: string;
}

export const ComparisonResult = (props: IProps): React.ReactElement => {
  const pdf = isPdf(props.pdfMode);
  const { result, entity, comparisonEntityName, colors } = props;

  const labels: React.ReactNode[] = [
    <LabelCell key='a' bgColor={colors[0]} style={pdf ? { flex: 1 } : undefined}>
      <div>{entity.name}</div>
      <div>
        <div>({`${result.dataSetA.count} Tests`})</div>
        <div>{result.dataSetA.timeframe}</div>
      </div>
    </LabelCell>,
    <LabelCell key='b' bgColor={colors[1]} style={pdf ? { flex: 1 } : undefined}>
      <div>{comparisonEntityName}</div>
      <div>
        <div>({`${result.dataSetB.count} Tests`})</div>
        <div>{result.dataSetB.timeframe}</div>
      </div>
    </LabelCell>,
    <LabelCell key='pct' center style={pdf ? { flex: 1 } : undefined}>% Difference</LabelCell>,
    <LabelCell key='td' center last style={pdf ? { flex: 1 } : undefined}>Total Difference</LabelCell>,
  ];

  const values: React.ReactNode[] = [
    <DataCell key='a' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.dataSetA.aggregate?.mean, result.dataSetA.aggregate?.roundingPrecision)}
        units={result.dataSetA.aggregate?.units}
      />
    </DataCell>,
    <DataCell key='b' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.dataSetB.aggregate?.mean, result.dataSetB.aggregate?.roundingPrecision)}
        units={result.dataSetB.aggregate?.units}
      />
    </DataCell>,
    <DataCell key='pct' lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.percentDifference, 2) ?? '--'}
        units='%'
        isSecondary
      />
    </DataCell>,
    <DataCell key='td' lastInRow lastRow={pdf}>
      <Metric
        style={{ alignItems: 'center' }}
        value={roundValue(result.change, result.dataSetA.aggregate?.roundingPrecision)}
        units={result.dataSetA.aggregate?.units}
        isSecondary
      />
    </DataCell>,
  ];

  return (
    <Notable pdf={pdf}>
      <Card
        style={{ marginTop: 'var(--grid-gap)', ...(pdf ? { height: 'auto', minHeight: 0 } : {}) }}
        inverse={shouldInvert(props.pdfMode)}
      >
        <Column>
          <CardHeader surface={24}>
            <div>{result.metric.label}</div>
          </CardHeader>
          {pdf ? (
            <Column>
              <Row style={{ alignItems: 'stretch' }}>{labels}</Row>
              <Row style={{ alignItems: 'stretch' }}>{values}</Row>
            </Column>
          ) : (
            <Row style={{ height: '100%' }} shouldCollapse>
              {labels.map((label, i) => (
                <DataColumn key={i}>
                  {label}
                  {values[i]}
                </DataColumn>
              ))}
            </Row>
          )}
        </Column>
      </Card>
    </Notable>
  );
};
